import {
  CButton,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSwitch,
} from "@coreui/react";
import { useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createWebhookEndpoint,
  updateWebhookEndpoint,
} from "../../api/webhook-endpoints";
import WebhookEndpoint, {
  WEBHOOK_EVENT_TYPE_LIST,
  newWebhookEndpoint,
} from "../../models/webhook-endpoint";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert } from "../utils/messages";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface WebhookEndpointFormProps {
  endpoint?: WebhookEndpoint;
  onCancel: () => void | Promise<void>;
  onSuccess: (_?: WebhookEndpoint) => void | Promise<void>;
}

const WebhookEndpointForm: React.FC<WebhookEndpointFormProps> = ({
  endpoint,
  onCancel,
  onSuccess,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const isEditing = endpoint !== undefined && endpoint.id !== undefined;
  const endpointRef = useRef<WebhookEndpoint>(
    endpoint ?? newWebhookEndpoint(taxPayer.id),
  );
  const [submitting, setSubmitting] = useState(false);
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [name, setName] = useState(endpoint?.name ?? "");
  const [isActive, setIsActive] = useState(endpoint?.isActive ?? true);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(
    (endpoint?.eventTypes ?? []).filter((t) =>
      WEBHOOK_EVENT_TYPE_LIST.some((e) => e.value === t),
    ),
  );

  const groups = Array.from(
    new Set(WEBHOOK_EVENT_TYPE_LIST.map((e) => e.group)),
  );

  const toggleEventType = (value: string) => {
    setSelectedEventTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  };

  const toggleGroup = (group: string) => {
    const groupTypes = WEBHOOK_EVENT_TYPE_LIST.filter(
      (e) => e.group === group,
    ).map((e) => e.value);
    const allSelected = groupTypes.every((t) => selectedEventTypes.includes(t));
    if (allSelected) {
      setSelectedEventTypes((prev) =>
        prev.filter((t) => !groupTypes.includes(t)),
      );
    } else {
      setSelectedEventTypes((prev) => [
        ...prev,
        ...groupTypes.filter((t) => !prev.includes(t)),
      ]);
    }
  };

  const onSubmit = async () => {
    if (!name) {
      errorAlert("El nombre del webhook es obligatorio.");
      return;
    }
    if (!url) {
      errorAlert("La URL del webhook es obligatoria.");
      return;
    }
    if (!url.startsWith("https://")) {
      errorAlert("La URL debe comenzar con https://");
      return;
    }
    if (selectedEventTypes.length === 0) {
      errorAlert("Debe seleccionar al menos un tipo de evento.");
      return;
    }

    setSubmitting(true);

    let status: any;
    if (isEditing) {
      status = await updateWebhookEndpoint(endpoint!.id!, {
        name,
        url,
        isActive,
        eventTypes: selectedEventTypes,
      });
    } else {
      const payload: WebhookEndpoint = {
        ...endpointRef.current,
        taxPayerId: taxPayer.id,
        name,
        url,
        isActive,
        eventTypes: selectedEventTypes,
      };
      status = await createWebhookEndpoint(payload);
    }

    if (status.status !== SUCCESS) {
      const msg =
        status.detail ??
        (isEditing
          ? "Error al actualizar el webhook."
          : "Error al crear el webhook.");
      errorAlert(msg);
      setSubmitting(false);
      return;
    }

    onSuccess(status.data as WebhookEndpoint);
    setSubmitting(false);
  };

  return (
    <>
      <CRow>
        <CCol sm="12">
          <div className="alert alert-info mb-3">
            <i className="fa fa-circle-nodes mr-2"></i>
            <strong>¿Para qué sirven los webhooks?</strong> Permiten que tu
            sistema externo reciba notificaciones automáticas en tiempo real
            cuando ocurren eventos en tus documentos electrónicos — como una
            factura aprobada o cancelada. Tu servidor recibirá un HTTP POST con
            los datos del evento firmado con HMAC-SHA256.
          </div>
        </CCol>
      </CRow>
      <CRow>
        <CCol sm="12" md="6">
          <CFormGroup>
            <CLabel>Nombre</CLabel>
            <CInput
              type="text"
              placeholder="Ej: Integración ERP"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setName(e.target.value);
              }}
            />
          </CFormGroup>
        </CCol>
        <CCol sm="12" md="6">
          <CFormGroup>
            <CLabel>URL (debe comenzar con https://)</CLabel>
            <CInput
              type="text"
              placeholder="https://mi-servidor.com/webhook"
              value={url}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUrl(e.target.value);
              }}
            />
          </CFormGroup>
        </CCol>
      </CRow>

      {isEditing && (
        <CRow>
          <CCol sm="12" md="6">
            <CFormGroup>
              <CLabel className="mr-3">Activo</CLabel>
              <CSwitch
                color="success"
                checked={isActive}
                onChange={() => setIsActive((prev) => !prev)}
              />
            </CFormGroup>
          </CCol>
        </CRow>
      )}

      <CRow>
        <CCol sm="12">
          <CLabel>
            <strong>Tipos de evento</strong>
          </CLabel>
          <CRow>
            {groups.map((group) => {
              const groupTypes = WEBHOOK_EVENT_TYPE_LIST.filter(
                (e) => e.group === group,
              );
              const allSelected = groupTypes.every((e) =>
                selectedEventTypes.includes(e.value),
              );
              return (
                <CCol key={group} sm="12" md="6" lg="4" className="mb-3">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center mb-1">
                      <input
                        type="checkbox"
                        id={`group-${group}`}
                        checked={allSelected}
                        onChange={() => toggleGroup(group)}
                        className="mr-2"
                        style={{ cursor: "pointer" }}
                      />
                      <CLabel
                        htmlFor={`group-${group}`}
                        className="mb-0 font-weight-bold"
                        style={{ cursor: "pointer" }}
                      >
                        {group}
                      </CLabel>
                    </div>
                    {groupTypes.map((eventType) => (
                      <div
                        key={eventType.value}
                        className="d-flex align-items-center ml-3"
                      >
                        <input
                          type="checkbox"
                          id={`event-${eventType.value}`}
                          checked={selectedEventTypes.includes(eventType.value)}
                          onChange={() => toggleEventType(eventType.value)}
                          className="mr-2"
                          style={{ cursor: "pointer" }}
                        />
                        <CLabel
                          htmlFor={`event-${eventType.value}`}
                          className="mb-0"
                          style={{ cursor: "pointer" }}
                        >
                          {eventType.label}
                        </CLabel>
                      </div>
                    ))}
                  </div>
                </CCol>
              );
            })}
          </CRow>
        </CCol>
      </CRow>

      <CRow className="mt-3">
        <CCol sm="12" className="d-flex justify-content-end">
          <CButton
            color="secondary"
            className="mr-2"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </CButton>
          <CButton color="primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <></>
            )}
            {submitting
              ? isEditing
                ? "Guardando..."
                : "Creando..."
              : isEditing
                ? "Guardar cambios"
                : "Crear Webhook"}
          </CButton>
        </CCol>
      </CRow>
    </>
  );
};

export default WebhookEndpointForm;
